// src/services/catalogStorageService.js
/**
 * catalogStorageService.js — Gerenciador de Cache Persistente em IndexedDB
 * 
 * Permite que o catálogo de mais de 3.800 produtos seja carregado em < 50ms
 * a partir do disco local do navegador, eliminando o delay inicial e divergências.
 * 
 * Totalmente isolado, nativo (sem dependências externas) e com fallback seguro.
 */

const DB_NAME = "semprejoias_catalog_db";
const DB_VERSION = 1;
const STORE_CATALOG = "catalog_records";
const STORE_META = "catalog_meta";

let dbInstance = null;

const isIndexedDBAvailable = () => {
  return typeof window !== "undefined" && "indexedDB" in window && window.indexedDB !== null;
};

const getDB = () => {
  if (!isIndexedDBAvailable()) {
    return Promise.resolve(null);
  }

  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }

  return new Promise((resolve) => {
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Store para registros individuais do catálogo (chave primária: id do PocketBase)
        if (!db.objectStoreNames.contains(STORE_CATALOG)) {
          const store = db.createObjectStore(STORE_CATALOG, { keyPath: "id" });
          store.createIndex("sku", "sku", { unique: false });
          store.createIndex("updated", "updated", { unique: false });
        }

        // Store para metadados de sincronização (lastSyncTime, moldesMap, estoqueCeraMap)
        if (!db.objectStoreNames.contains(STORE_META)) {
          db.createObjectStore(STORE_META, { keyPath: "key" });
        }
      };

      request.onsuccess = (event) => {
        dbInstance = event.target.result;
        
        // Tratamento para caso o banco seja fechado externamente
        dbInstance.onclose = () => {
          dbInstance = null;
        };

        resolve(dbInstance);
      };

      request.onerror = (event) => {
        console.warn("[catalogStorageService] Erro ao abrir IndexedDB. Fallback ativo:", event.target.error);
        resolve(null);
      };
    } catch (err) {
      console.warn("[catalogStorageService] Exceção ao abrir IndexedDB:", err);
      resolve(null);
    }
  });
};

export const catalogStorageService = {
  /**
   * Recupera todos os dados do catálogo salvos no IndexedDB.
   * Retorna { records, moldesMap, estoqueMap, lastSyncTime, count } ou null se vazio.
   */
  async getCachedCatalogData() {
    const db = await getDB();
    if (!db) return null;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([STORE_CATALOG, STORE_META], "readonly");
        const catalogStore = tx.objectStore(STORE_CATALOG);
        const metaStore = tx.objectStore(STORE_META);

        const recordsReq = catalogStore.getAll();
        const lastSyncReq = metaStore.get("lastSyncTime");
        const moldesReq = metaStore.get("moldesMap");
        const estoqueReq = metaStore.get("estoqueMap");

        tx.oncomplete = () => {
          const records = recordsReq.result || [];
          if (records.length === 0) {
            resolve(null);
            return;
          }

          resolve({
            records,
            count: records.length,
            lastSyncTime: lastSyncReq.result?.value || null,
            moldesMap: moldesReq.result?.value || {},
            estoqueMap: estoqueReq.result?.value || {}
          });
        };

        tx.onerror = (err) => {
          console.warn("[catalogStorageService] Erro ao ler dados do cache:", err);
          resolve(null);
        };
      } catch (e) {
        console.warn("[catalogStorageService] Falha na transação de leitura:", e);
        resolve(null);
      }
    });
  },

  /**
   * Salva ou substitui o catálogo completo de uma vez (carga inicial ou refresh forçado).
   */
  async saveFullCatalog(records, moldesMap = {}, estoqueMap = {}, lastSyncTime = null) {
    const db = await getDB();
    if (!db || !Array.isArray(records)) return false;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([STORE_CATALOG, STORE_META], "readwrite");
        const catalogStore = tx.objectStore(STORE_CATALOG);
        const metaStore = tx.objectStore(STORE_META);

        // Limpa store antes de regravar
        catalogStore.clear();

        // Insere todos os registros
        for (let i = 0; i < records.length; i++) {
          const rec = records[i];
          if (rec && rec.id) {
            catalogStore.put(rec);
          }
        }

        // Salva metadados
        metaStore.put({ key: "lastSyncTime", value: lastSyncTime || new Date().toISOString() });
        metaStore.put({ key: "moldesMap", value: moldesMap });
        metaStore.put({ key: "estoqueMap", value: estoqueMap });

        tx.oncomplete = () => {
          console.log(`[catalogStorageService] ✅ Cache atualizado com sucesso (${records.length} itens).`);
          resolve(true);
        };

        tx.onerror = (err) => {
          console.warn("[catalogStorageService] Erro ao gravar catálogo completo:", err);
          resolve(false);
        };
      } catch (e) {
        console.warn("[catalogStorageService] Falha na transação de gravação:", e);
        resolve(false);
      }
    });
  },

  /**
   * Aplica atualizações incrementais (Delta-Sync): adiciona/atualiza registros e remove deletados.
   */
  async applyDeltaUpdates(updatedRecords = [], deletedIds = [], lastSyncTime = null) {
    const db = await getDB();
    if (!db) return false;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([STORE_CATALOG, STORE_META], "readwrite");
        const catalogStore = tx.objectStore(STORE_CATALOG);
        const metaStore = tx.objectStore(STORE_META);

        // Insere ou atualiza
        (updatedRecords || []).forEach((rec) => {
          if (rec && rec.id) {
            catalogStore.put(rec);
          }
        });

        // Deleta itens removidos
        (deletedIds || []).forEach((id) => {
          if (id) {
            catalogStore.delete(id);
          }
        });

        // Atualiza timestamp da última sincronização
        if (lastSyncTime) {
          metaStore.put({ key: "lastSyncTime", value: lastSyncTime });
        }

        tx.oncomplete = () => {
          resolve(true);
        };

        tx.onerror = (err) => {
          console.warn("[catalogStorageService] Erro ao aplicar delta:", err);
          resolve(false);
        };
      } catch (e) {
        console.warn("[catalogStorageService] Falha na transação de delta:", e);
        resolve(false);
      }
    });
  },

  /**
   * Salva apenas os mapas de moldes e estoques de cera (atualizações em tempo real)
   */
  async saveMoldesAndCera(moldesMap, estoqueMap) {
    const db = await getDB();
    if (!db) return false;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([STORE_META], "readwrite");
        const metaStore = tx.objectStore(STORE_META);
        if (moldesMap) metaStore.put({ key: "moldesMap", value: moldesMap });
        if (estoqueMap) metaStore.put({ key: "estoqueMap", value: estoqueMap });

        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      } catch (e) {
        resolve(false);
      }
    });
  },

  /**
   * Limpa o banco de cache local (para reset ou logout)
   */
  async clearCache() {
    const db = await getDB();
    if (!db) return false;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction([STORE_CATALOG, STORE_META], "readwrite");
        tx.objectStore(STORE_CATALOG).clear();
        tx.objectStore(STORE_META).clear();

        tx.oncomplete = () => {
          console.log("[catalogStorageService] Cache limpo.");
          resolve(true);
        };
        tx.onerror = () => resolve(false);
      } catch (e) {
        resolve(false);
      }
    });
  }
};
